import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { formatDate } from "../utils/format";
import toast from "react-hot-toast";

export default function AdminReturnsPage() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [refundAmt, setRefundAmt] = useState("");
  const [note, setNote] = useState("");

  async function load() {
    const snap = await getDocs(query(collection(db, "returns"), orderBy("createdAt", "desc")));
    setReturns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function processReturn(id, status) {
    await updateDoc(doc(db, "returns", id), {
      status,
      refundAmount: status === "approved" && refundAmt ? Number(refundAmt) : null,
      adminNote: note,
      processedAt: serverTimestamp(),
    });
    toast.success(`Return ${status}`);
    setSelected(null);
    setRefundAmt("");
    setNote("");
    load();
  }

  const BADGE = { pending: "badge-warning", approved: "badge-success", rejected: "badge-danger", completed: "badge-info" };

  return (
    <div style={{ padding: "40px 48px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Return Requests</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>Review and process customer returns and refunds.</p>
      </div>

      <div className={selected ? "admin-detail-layout" : ""} style={{ display: "grid", gridTemplateColumns: selected ? undefined : "1fr", gap: 24, transition: "all 0.3s ease" }}>
        <div className="card" style={{ overflow: "hidden", background: "#fff", border: "1px solid var(--border)" }}>
          {loading ? <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                    {["Order", "Customer", "Reason", "Date", "Status", ""].map(h => (
                      <th key={h} style={{ padding: "16px 24px", textAlign: "left", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {returns.map(r => (
                    <tr key={r.id} onClick={() => setSelected(r)} style={{ borderBottom: "1px solid var(--border)", cursor: "pointer", background: selected?.id === r.id ? "rgba(232, 71, 26, 0.04)" : "", transition: "background 0.2s" }}
                      onMouseEnter={e => { if (selected?.id !== r.id) e.currentTarget.style.background = "#F8FAFC"; }}
                      onMouseLeave={e => { if (selected?.id !== r.id) e.currentTarget.style.background = ""; }}>
                      <td style={{ padding: "16px 24px", fontWeight: 600, fontFamily: "monospace", fontSize: 15 }}>#{r.orderId?.slice(0, 8).toUpperCase()}</td>
                      <td style={{ padding: "16px 24px", fontWeight: 500, color: "var(--text)" }}>{r.userEmail}</td>
                      <td style={{ padding: "16px 24px", color: "var(--text-muted)", fontSize: 14 }}>{r.reason}</td>
                      <td style={{ padding: "16px 24px", fontSize: 14, color: "var(--text-muted)" }}>{formatDate(r.createdAt)}</td>
                      <td style={{ padding: "16px 24px" }}><span className={`badge ${BADGE[r.status] || "badge-default"}`} style={{ textTransform: "capitalize", padding: "6px 12px", fontSize: 12, fontWeight: 700 }}>{r.status}</span></td>
                      <td style={{ padding: "16px 24px", color: "var(--brand)", fontSize: 14, fontWeight: 600 }}>Review →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && returns.length === 0 && <p style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>No return requests yet.</p>}
        </div>

        {selected && (
          <div className="card" style={{ padding: 28, position: "sticky", top: 24, background: "#fff", border: "1px solid var(--border)", boxShadow: "var(--shadow-md)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
              <div>
                <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 18 }}>Return Details</h3>
                <span className={`badge ${BADGE[selected.status]}`} style={{ textTransform: "capitalize", padding: "4px 8px", fontSize: 11, fontWeight: 700, marginTop: 8 }}>{selected.status}</span>
              </div>
              <button onClick={() => setSelected(null)} style={{ border: "none", background: "var(--surface)", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18, color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.color = "#EF4444"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.color = "var(--text-muted)"; }}>×</button>
            </div>
            
            <div style={{ display: "flex", flexDirection: "column", gap: 16, fontSize: 14, marginBottom: 28 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><strong style={{ color: "var(--text-muted)" }}>Order</strong> <span style={{ fontFamily: "monospace", fontSize: 15, fontWeight: 600 }}>#{selected.orderId?.slice(0, 8).toUpperCase()}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><strong style={{ color: "var(--text-muted)" }}>Customer</strong> <span style={{ fontWeight: 500 }}>{selected.userEmail}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><strong style={{ color: "var(--text-muted)" }}>Item</strong> <span style={{ fontWeight: 500 }}>{selected.orderItem || "Entire order"}</span></div>
              <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />
              <div>
                <strong style={{ display: "block", color: "var(--text-muted)", marginBottom: 6 }}>Reason for return:</strong>
                <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8, color: "var(--text)" }}>{selected.reason}</div>
              </div>
              {selected.details && (
                <div>
                  <strong style={{ display: "block", color: "var(--text-muted)", marginBottom: 6 }}>Additional Details:</strong>
                  <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8, color: "var(--text-muted)", fontStyle: "italic" }}>{selected.details}</div>
                </div>
              )}
            </div>

            {selected.status === "pending" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 20, background: "var(--surface)", borderRadius: 12, border: "1px solid var(--border)" }}>
                <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Refund Amount (₦) — if approving</label>
                  <input className="form-input" type="number" min={0} placeholder="e.g. 50000" value={refundAmt} onChange={e => setRefundAmt(e.target.value)} style={{ padding: "10px 14px", borderRadius: 8 }} />
                </div>
                <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Admin Note (sent to customer)</label>
                  <textarea className="form-input" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Explain the decision…" style={{ resize: "vertical", padding: "12px", borderRadius: 8 }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
                  <button onClick={() => processReturn(selected.id, "approved")} className="btn btn-lg" style={{ background: "#10B981", color: "#fff", border: "none", fontSize: 14 }}>✓ Approve</button>
                  <button onClick={() => processReturn(selected.id, "rejected")} className="btn btn-lg" style={{ background: "#EF4444", color: "#fff", border: "none", fontSize: 14 }}>✗ Reject</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
